import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, Fragment, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { createUseStyles, RadioGroup, Multiplier, Text, Advice } from 'hoi-poi-ui';
import { useWeb5Context } from "../../../../lib/Web5Context";
import { getImportSchema, getImport, updateSchema, runImport } from "../../../../lib/services/imports";
import { showErrorToast } from "../../../../lib/errors";
import ModalConfirm from "../../../../components/ModalConfirm";
import StepContent from "../StepContent";
import LimitsModal from "../LimitsModal";
import AvoidDuplicatesIcon from "../../icons/AvoidDuplicatesIcon";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'AvoidDuplicates');
var _ref2 = /*#__PURE__*/React.createElement(AvoidDuplicatesIcon, null);
const AvoidDuplicates = _ref => {
  let {
    entityName,
    entityLogName,
    entityLabel,
    data,
    onImported,
    onBack,
    logEvent,
    onCancelConfirm,
    duplicatesForm,
    setDuplicatesForm,
    modalConfirmIsOpen,
    modalLimitsIsOpen,
    limits,
    onConfirmLimits,
    onCancelLimits
  } = _ref;
  const classes = useStyles();
  const isUsers = entityName === 'users';
  const {
    t
  } = useWeb5Context();
  const timeout = useRef();
  const fieldsQuestion = useRef();
  const [fieldOptions, setFieldOptions] = useState([]);
  const [schema, setSchema] = useState(data.schema);
  const [isProcessing, setIsProcessing] = useState(false);

  // Options
  const checkOptions = useMemo(() => [{
    label: t('cfm_action_yes'),
    value: true
  }, {
    label: t('cfm_action_no'),
    value: false
  }], [t]);
  const filterOption = useCallback((option, searchText) => {
    let isValid = duplicatesForm.fields && !duplicatesForm.fields.find(field => field && field.value === option.value) && schema.matchingSchema.find(field => field.nameField && field.nameField === option.value);
    if (option.label) {
      isValid = isValid && option.label.match(new RegExp(searchText, 'ig'));
    }
    return isValid;
  }, [duplicatesForm.fields, schema]);
  const fieldSchema = useMemo(() => ({
    name: 'fields',
    type: 'select',
    placeholder: t('cfm_placeholder_choose_field'),
    attrs: {
      options: fieldOptions,
      overrides: {
        'react-select': {
          filterOption
        }
      }
    }
  }), [fieldOptions, filterOption, t]);
  const typeOptions = useMemo(() => [{
    label: t('cfm_label_duplicate_override_all'),
    value: '0'
  }, {
    label: t('cfm_label_duplicate_right_empty_values'),
    value: '1'
  }, {
    label: t('cfm_label_duplicate_right_keep_empty_values'),
    value: '2'
  }], [t]);
  const pollingState = useCallback((data, state, cb, cbErr) => {
    if (!Array.isArray(state)) state = [state];
    timeout.current = setTimeout(() => {
      getImport(data.id).then(result => {
        return state.includes(result.data.state) ? cb && cb(result.data) : pollingState(result.data, state, cb, cbErr);
      }).catch(error => {
        cbErr && cbErr();
        showErrorToast(t, error);
      });
    }, 2000);
  }, [t]);
  useEffect(() => {
    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
  }, []);

  // Get schema
  useEffect(() => {
    function formatData(data) {
      let fields = (data.duplicateSchema.fields || []).map(field => ({
        label: field,
        value: field
      }));
      setDuplicatesForm({
        enabled: data.duplicateSchema.enabled || true,
        type: data.duplicateSchema.type,
        fields: fields.length ? fields : [null]
      });
      setFieldOptions(data.matchingSchema.map(item => ({
        label: item.name,
        value: item.nameField
      })));
    }

    // From previous step
    if (schema) {
      return formatData(schema);
    }

    // Updating schema
    if (data.state === 1) return pollingState(data, 1);

    // Run in progress
    if (data.state === 4) return pollingState(data, 4);
    getImportSchema(data.id).then(result => {
      formatData(result.data);
      setSchema(result.data);
    }).catch(error => {
      console.error(error);
      showErrorToast(t, error);
      onBack();
    });
  }, [data, isUsers, onBack, pollingState, schema, t, setDuplicatesForm]);
  const onConfirm = useCallback(() => {
    setIsProcessing(true);
    logEvent({
      event: entityLogName,
      submodule: entityName,
      functionality: 'importCreateFinish'
    });
    let newDuplicateSchema = _objectSpread(_objectSpread({}, schema.duplicateSchema), {}, {
      enabled: duplicatesForm.enabled,
      type: duplicatesForm.type
    });
    let cleanFields = duplicatesForm.fields.filter(field => field).map(field => field.value);
    if (cleanFields.length) newDuplicateSchema.fields = cleanFields;

    // Merge current matching values
    const newSchema = _objectSpread(_objectSpread({}, schema), {}, {
      duplicateSchema: newDuplicateSchema
    });
    updateSchema(data.id, newSchema).then(() => runImport(data.id)).then(() => onImported(data.id)).catch(error => {
      console.error(error);
      showErrorToast(t, error);
    });
  }, [logEvent, entityLogName, entityName, schema, duplicatesForm.enabled, duplicatesForm.type, duplicatesForm.fields, data, onImported, t]);

  // Form handlers
  const changeCheck = useCallback(option => {
    setDuplicatesForm(_objectSpread(_objectSpread({}, duplicatesForm), {}, {
      enabled: option
    }));
  }, [setDuplicatesForm, duplicatesForm]);
  const changeType = useCallback(option => {
    setDuplicatesForm(_objectSpread(_objectSpread({}, duplicatesForm), {}, {
      type: parseInt(option, 10)
    }));
  }, [setDuplicatesForm, duplicatesForm]);
  const changeField = useCallback(fields => {
    setDuplicatesForm(_objectSpread(_objectSpread({}, duplicatesForm), {}, {
      fields
    }));
    if (fields.length !== duplicatesForm.fields.length && fieldsQuestion.current) {
      fieldsQuestion.current.scrollIntoView(true);
    }
  }, [setDuplicatesForm, duplicatesForm]);
  const modalConfirmProps = useMemo(() => {
    return {
      onConfirm,
      onCancel: onCancelConfirm,
      isOpen: modalConfirmIsOpen,
      title: `${t('cfm_lable_confirm_import_title')} ${t('cfm_label_import_type_create', [entityLabel])}`,
      description: t('cfm_confirm_info_num_entity_create', [data.rows, entityLabel]),
      advice: t('label_integration_disclaimer_import_create'),
      writeXToConfirm: 'label_type_confirmation',
      passPhrase: t('cfm_label_create'),
      confirmText: t('cfm_action_confirm_import_create', [data.rows, entityLabel]),
      isConfirmLoading: isProcessing
    };
  }, [data.rows, entityLabel, isProcessing, modalConfirmIsOpen, onCancelConfirm, onConfirm, t]);

  // Dynamic form
  const showFields = duplicatesForm.enabled && !isUsers;
  const showFounded = isUsers || duplicatesForm.enabled && duplicatesForm.fields && !!duplicatesForm.fields.find(field => !!field);
  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.stepWrapper
  }, /*#__PURE__*/React.createElement(StepContent, null, /*#__PURE__*/React.createElement("div", {
    className: classes.row
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.rowIcon
  }, _ref2), /*#__PURE__*/React.createElement(Text, {
    bold: true
  }, t('cfm_label_duplicate_question_1'))), isUsers && /*#__PURE__*/React.createElement(Advice, {
    type: "info",
    className: classes.duplicatesSubtitle,
    showCollapse: false
  }, t('cfm_label_warning_users_duplicate_management')), !isUsers && /*#__PURE__*/React.createElement("div", {
    className: classes.duplicatesSubtitle
  }, /*#__PURE__*/React.createElement(Text, null, t('cfm_label_duplicate_desc_1'))), !isUsers && /*#__PURE__*/React.createElement("div", {
    className: classes.duplicatesQuestion
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true
  }, t('cfm_label_duplicate_question_2')), /*#__PURE__*/React.createElement(RadioGroup, {
    options: checkOptions,
    onChange: changeCheck,
    value: duplicatesForm.enabled
  })), showFields && /*#__PURE__*/React.createElement("div", {
    className: classes.duplicatesFieldsQuestion
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true
  }, t('cfm_label_duplicate_question_3')), /*#__PURE__*/React.createElement(Multiplier, {
    buttonLabel: t('cfm_action_add_field'),
    value: duplicatesForm.fields,
    schema: fieldSchema,
    separator: false,
    onChange: changeField,
    classes: {
      item: classes.duplicatesFieldsItem,
      buttonContainer: classes.duplicatesFieldButton
    }
  })), showFounded && /*#__PURE__*/React.createElement("div", {
    ref: fieldsQuestion,
    className: classes.duplicatesQuestion
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true
  }, t('cfm_label_duplicate_question_4')), /*#__PURE__*/React.createElement(RadioGroup, {
    options: typeOptions,
    onChange: changeType,
    value: duplicatesForm.type.toString(),
    isFullWidth: true
  })))), /*#__PURE__*/React.createElement(LimitsModal, {
    isOpen: modalLimitsIsOpen,
    onConfirm: onConfirmLimits,
    onCancel: onCancelLimits,
    limits: limits,
    data: data
  }), /*#__PURE__*/React.createElement(ModalConfirm, modalConfirmProps));
};
process.env.NODE_ENV !== "production" ? AvoidDuplicates.propTypes = {} : void 0;
export default /*#__PURE__*/memo(AvoidDuplicates);